import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminEconomicsInput } from '../lib/api';
import { useOperatorPermissions } from '../hooks/useOperatorPermissions';

export function EconomicsPage({ product }: { product: 'APP' | 'STUDIO' }) {
  const permissions = useOperatorPermissions();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['economics', product],
    queryFn: () => adminApi.getEconomics(product),
  });
  const [form, setForm] = useState<AdminEconomicsInput | null>(null);
  const source = form ?? (data ? {
    usdRubRate: data.published.usd_rub_rate,
    appGrossMarginPercent: data.published.app_gross_margin_percent,
    studioGrossMarginPercent: data.published.studio_gross_margin_percent,
    rubPerCredit: data.published.rub_per_credit,
  } : null);
  const draft = useMutation({
    mutationFn: (input: AdminEconomicsInput) => adminApi.createEconomicsDraft(input),
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['economics'] });
    },
  });
  const publish = useMutation({
    mutationFn: () => adminApi.publishEconomicsDraft(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['economics'] }),
  });

  if (isLoading) return <p className="text-sm text-ops-muted">Загрузка экономики…</p>;
  if (error || !data || !source) return <p className="text-sm text-red-600">Экономическая политика недоступна.</p>;

  const margin = Number(product === 'APP' ? source.appGrossMarginPercent : source.studioGrossMarginPercent);
  const cogsShare = 100 - margin;
  const coefficient = cogsShare > 0 ? 100 / cogsShare : 0;
  const set = (key: keyof AdminEconomicsInput, value: string) => setForm({ ...source, [key]: value });
  const canEdit = permissions.canManageEconomics;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Экономика</h1>
        <p className="text-sm text-ops-muted">Версия v{data.published.version} · опубликовано {data.published.published_at ?? '—'}</p>
      </div>
      <section className="ops-card space-y-4 p-4">
        <label className="block text-sm font-medium">Расчётный курс USD → RUB
          <input className="mt-1 block w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2" value={source.usdRubRate} disabled={!canEdit} onChange={(e) => set('usdRubRate', e.target.value)} />
        </label>
        <label className="block text-sm font-medium">{product === 'APP' ? 'APP' : 'Studio'} — валовая маржа, %
          <input className="mt-1 block w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2" value={product === 'APP' ? source.appGrossMarginPercent : source.studioGrossMarginPercent} disabled={!canEdit} onChange={(e) => set(product === 'APP' ? 'appGrossMarginPercent' : 'studioGrossMarginPercent', e.target.value)} />
        </label>
        <label className="block text-sm font-medium">1 Prisma Credit, ₽
          <input className="mt-1 block w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2" value={source.rubPerCredit} disabled={!canEdit} onChange={(e) => set('rubPerCredit', e.target.value)} />
        </label>
        <div className="rounded-lg bg-ops-surface p-3 text-sm">
          <p>COGS: {cogsShare.toFixed(2)}%</p>
          <p>Коэффициент цены: ×{coefficient.toFixed(2)}</p>
          <p>Наценка к COGS: +{(coefficient > 0 ? (coefficient - 1) * 100 : 0).toFixed(2)}%</p>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="rounded-lg border border-ops-border px-3 py-2 text-sm" disabled={draft.isPending || Boolean(data.draft)} onClick={() => draft.mutate(source)}>{data.draft ? `Draft v${data.draft.version}` : 'Создать draft'}</button>
            <button type="button" className="rounded-lg bg-ops-accent px-3 py-2 text-sm text-white" disabled={publish.isPending || !data.draft} onClick={() => publish.mutate()}>{publish.isPending ? 'Публикация…' : 'Опубликовать'}</button>
          </div>
        ) : null}
        {draft.error || publish.error ? <p className="text-sm text-red-600">Не удалось сохранить policy.</p> : null}
      </section>
    </div>
  );
}
